import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Home as HomeIcon, Clock, MapPin, Mail, Loader2 } from "lucide-react";
import { PageHero, Reveal } from "@/components/site/Motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROFILS = [
  { value: "famille", label: "Une famille" },
  { value: "structure", label: "Une structure (école, association…)" },
  { value: "chien", label: "Propriétaire d'un chien ou d'un chiot" },
];

const INFOS = [
  { icon: Mail, title: "Par email", text: "sophie_reitzer@hotmail.fr — écrivez-moi directement." },
  { icon: HomeIcon, title: "À votre domicile", text: "Les séances ont lieu chez vous, là où vivent les problématiques." },
  { icon: MapPin, title: "Zone d'intervention", text: "Coutances · Saint-Lô · Granville · Portbail-sur-Mer et les communes alentour." },
  { icon: Clock, title: "Réponse sous 48 h", text: "Je vous recontacte rapidement pour un premier échange gratuit." },
];

export default function Contact() {
  const [form, setForm] = useState({ nom: "", email: "", telephone: "", message: "", profil: "famille" });
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        nom: form.nom.trim(),
        email: form.email.trim(),
        telephone: form.telephone.trim() || null,
        message: form.message.trim(),
        profil: form.profil,
      });
      toast.success("Message bien reçu !", {
        description: "Je vous recontacte sous 48 h pour un premier échange.",
      });
      setForm({ nom: "", email: "", telephone: "", message: "", profil: "famille" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Le message n'a pas pu être envoyé", {
        description: typeof detail === "string" ? detail : "Vérifiez les champs et réessayez.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        testId="contact-hero"
        overline="Contact"
        lines={[
          { text: "Laissez-nous" },
          { text: "un message.", accent: true },
        ]}
        intro="Racontez-moi votre situation en quelques lignes. Le premier échange téléphonique est gratuit et sans engagement."
      />

      <section className="px-6 pb-24 sm:px-10 md:pb-32 lg:px-16" data-testid="contact-section">
        <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-serif text-3xl font-medium leading-[1.15] tracking-tight text-ink sm:text-4xl">
                Un premier pas, <span className="italic text-terracotta">tout en douceur.</span>
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-ink/70">
                Tensions familiales, difficultés éducatives, adoption récente,
                relation compliquée avec votre chien, votre enfant, un parent…
                Chaque situation mérite une écoute attentive.
              </p>
            </Reveal>
            <div className="mt-10 space-y-5">
              {INFOS.map((info, i) => (
                <Reveal key={info.title} delay={0.08 * i}>
                  <div className="flex gap-5 rounded-3xl border border-stone-border bg-white/60 p-6 transition-transform duration-300 hover:-translate-y-1" data-testid={`contact-info-${i + 1}`}>
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-terracotta/10 text-terracotta">
                      <info.icon size={20} strokeWidth={1.8} />
                    </span>
                    <div>
                      <p className="font-serif text-xl font-medium text-ink">{info.title}</p>
                      <p className="mt-1 text-sm leading-relaxed text-ink/65">{info.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.15} y={44}>
              <form
                onSubmit={handleSubmit}
                className="rounded-[2rem] border border-stone-border bg-white/80 p-8 shadow-[0_30px_60px_-40px_rgba(43,58,48,0.35)] md:p-12"
                data-testid="contact-form"
              >
                <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-ink/60">Vous êtes *</p>
                <div className="flex flex-wrap gap-3" data-testid="contact-profil-group">
                  {PROFILS.map((p) => (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, profil: p.value }))}
                      className={`rounded-full border px-6 py-3 text-sm font-semibold transition-[background-color,color,border-color,transform] duration-300 active:scale-95 ${
                        form.profil === p.value
                          ? "border-terracotta bg-terracotta text-cream shadow-[0_10px_25px_-12px_rgba(200,90,50,0.6)]"
                          : "border-stone-border bg-white/60 text-ink/70 hover:-translate-y-0.5 hover:border-terracotta/50 hover:text-ink"
                      }`}
                      data-testid={`contact-profil-${p.value}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <div className="mt-7 grid gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-nom" className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-ink/60">
                      Votre nom *
                    </label>
                    <input
                      id="contact-nom"
                      data-testid="contact-input-nom"
                      className="input-field"
                      placeholder="Marie Dupont"
                      value={form.nom}
                      onChange={update("nom")}
                      required
                      minLength={2}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-ink/60">
                      Votre email *
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      data-testid="contact-input-email"
                      className="input-field"
                      placeholder="marie@exemple.fr"
                      value={form.email}
                      onChange={update("email")}
                      required
                    />
                  </div>
                </div>
                <div className="mt-6">
                  <label htmlFor="contact-telephone" className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-ink/60">
                    Téléphone <span className="font-normal normal-case tracking-normal text-ink/40">(optionnel)</span>
                  </label>
                  <input
                    id="contact-telephone"
                    type="tel"
                    data-testid="contact-input-telephone"
                    className="input-field"
                    placeholder="06 12 34 56 78"
                    value={form.telephone}
                    onChange={update("telephone")}
                  />
                </div>
                <div className="mt-6">
                  <label htmlFor="contact-message" className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-ink/60">
                    Votre message *
                  </label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-input-message"
                    className="input-field min-h-[160px] resize-y"
                    placeholder="Parlez-moi de votre famille, de votre animal et de ce que vous aimeriez apaiser…"
                    value={form.message}
                    onChange={update("message")}
                    required
                    minLength={10}
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  className="btn-primary mt-8 w-full disabled:cursor-not-allowed disabled:opacity-60"
                  data-testid="contact-submit-button"
                >
                  {sending ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Envoi en cours…
                    </>
                  ) : (
                    <>
                      Envoyer le message <Send size={15} />
                    </>
                  )}
                </button>
                <p className="mt-4 text-center text-xs leading-relaxed text-ink/45">
                  Vos informations restent confidentielles et ne servent qu'à vous recontacter.
                </p>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
